import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "../axiosConfig";
import { UserDetailsContext } from "./userDetailsContext";
import { ConfigLinksDetailsContext } from "./cofingLinksDetailsContext";

export const POLL_MS = 60 * 1000;
const MAX_POPUPS = 3;
const AUTO_ASK_KEY = "emun-notify-auto-asked";

export const NotificationsContext = createContext({
  unreadCount: 0,
  refresh: () => {},
  permission: "unsupported",
  requestPermission: () => {},
});

export const useNotifications = () => useContext(NotificationsContext);

// Android Chrome throws on `new Notification()` (it needs a service worker), and phones get the app's push anyway.
export const browserNotificationsSupported = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  !/Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

const plainText = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(String(html), "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
};

const sameUser = (a, b) => !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();

const dayString = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// One staff message is logged up to three times (portal, SMS, email), so new rows are grouped per request.
const groupNew = (rows) => {
  const groups = new Map();
  rows.forEach((row) => {
    const key = row.subscriberServiceId || `n${row.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return [...groups.values()];
};

export const NotificationsProvider = ({ children }) => {
  const { userDetails } = useContext(UserDetailsContext);
  const { configLinks } = useContext(ConfigLinksDetailsContext);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [permission, setPermission] = useState(
    browserNotificationsSupported() ? Notification.permission : "unsupported"
  );

  const isAuthenticated = !!userDetails?.isAuthenticated;
  const userId = userDetails?.userId;

  // Kept in refs so the interval and the notification click handlers always see the latest values.
  const navigateRef = useRef(navigate);
  const lastIdRef = useRef(null);
  const lastCountRef = useRef(null);
  const stoppedRef = useRef(false);
  const contextRef = useRef({});
  const refreshRef = useRef(() => {});
  navigateRef.current = navigate;
  contextRef.current = { t, lang: i18n.language, logo: configLinks?.logoUrl };

  const markRead = useCallback(async (ids) => {
    await Promise.all(
      ids.map((notificationId) => axios.post("/user/notifications/read", { notificationId }).catch(() => null))
    );
  }, []);

  const showPopup = useCallback(
    (group, onRead) => {
      const { t: tr, lang, logo } = contextRef.current;
      const row = group.find((n) => n.sentTo === "PortalOnly") || group.find((n) => !/<[a-z]/i.test(n.message || "")) || group[0];
      const number = row.subscriberServiceFullNumber ? ` - ${row.subscriberServiceFullNumber}` : "";
      try {
        const popup = new Notification(`${row.desc || tr("notif_browserTitle")}${number}`, {
          body: plainText(row.message).slice(0, 160),
          tag: row.subscriberServiceId ? `req-${row.subscriberServiceId}` : `notif-${row.id}`,
          dir: lang === "en" ? "ltr" : "rtl",
          lang: lang === "en" ? "en" : "ar",
          icon: logo ? `/${logo}` : "/attachments/Logos/logo.png",
        });
        popup.onclick = () => {
          window.focus();
          navigateRef.current(row.subscriberServiceId ? `/order-details/${row.subscriberServiceId}` : "/my-notifications");
          popup.close();
          onRead(group.map((n) => n.id));
        };
      } catch {
        // Some browsers only allow notifications from a service worker; the badge still shows the count.
      }
    },
    []
  );

  const checkNew = useCallback(async () => {
    const since = new Date();
    since.setDate(since.getDate() - 1);
    const rows = await axios.get(`/user/notifications?startDate=${dayString(since)}`);
    if (!Array.isArray(rows)) return;
    const maxId = rows.reduce((max, n) => Math.max(max, n.id || 0), lastIdRef.current || 0);
    const firstRun = lastIdRef.current === null;
    const fresh = firstRun
      ? []
      : rows.filter((n) => n.id > lastIdRef.current && !n.isRead && !sameUser(n.sentByUserId, userId));
    lastIdRef.current = maxId;
    if (fresh.length === 0 || Notification.permission !== "granted") return;
    groupNew(fresh)
      .slice(0, MAX_POPUPS)
      .forEach((group) =>
        showPopup(group, async (ids) => {
          await markRead(ids);
          refreshRef.current();
        })
      );
  }, [userId, showPopup, markRead]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || stoppedRef.current) {
      setUnreadCount(0);
      return;
    }
    try {
      const response = await axios.get("/user/notifications/unread-count");
      const count = response?.unreadCount ?? 0;
      setUnreadCount(count);
      const changed = count !== lastCountRef.current;
      lastCountRef.current = count;
      if (browserNotificationsSupported() && (changed || lastIdRef.current === null)) {
        await checkNew();
      }
    } catch (error) {
      // An expired session stops polling; anything else (429, network) just skips this tick.
      if (error?.status === 401) stoppedRef.current = true;
    }
  }, [isAuthenticated, checkNew]);

  refreshRef.current = refresh;

  useEffect(() => {
    stoppedRef.current = false;
    lastIdRef.current = null;
    lastCountRef.current = null;
  }, [userId]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return undefined;
    }
    const timer = setInterval(() => refreshRef.current(), POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshRef.current();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isAuthenticated]);

  // Same as before: the badge also refreshes on every page change.
  useEffect(() => {
    refreshRef.current();
  }, [isAuthenticated, location.pathname]);

  const requestPermission = useCallback(async () => {
    if (!browserNotificationsSupported()) return;
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
    } catch {
      setPermission(Notification.permission);
    }
  }, []);

  // Ask the browser once per session as soon as the citizen is signed in (Chrome shows its prompt; browsers that
  // only allow it from a click ignore this, and the banner's "تفعيل" button stays as the fallback).
  useEffect(() => {
    if (!isAuthenticated || !browserNotificationsSupported() || Notification.permission !== "default") return;
    try {
      if (sessionStorage.getItem(AUTO_ASK_KEY) === "1") return;
      sessionStorage.setItem(AUTO_ASK_KEY, "1");
    } catch {
      // Storage blocked: still ask, at worst once per page load.
    }
    requestPermission();
  }, [isAuthenticated, requestPermission]);

  return (
    <NotificationsContext.Provider value={{ unreadCount, refresh, permission, requestPermission }}>
      {children}
    </NotificationsContext.Provider>
  );
};
